import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../services/adminService';
import { useModal } from '../context/ModalContext';
import { hkDateISO, hkDateISOPlusDays } from '../utils/dateUtils';
import { isValidEmail, findInvalidEmails } from '../utils/emailUtils';
import { copyTextToClipboard } from '../utils/clipboard.js';

const formatDate = (dateStr) => {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  return d.toLocaleString('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).replace(',', '');
};

const FILTER_TABS = [
  { key: 'all', label: 'All', filter: () => true },
  { key: 'active', label: 'Active', filter: (l) => !l.isExpired },
  { key: 'expired', label: 'Expired', filter: (l) => l.isExpired === true },
];

// ── Styles ──────────────────────────────────────────────────────────────────
const styles = {
  page: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  headerBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  headerTitle: { margin: 0, fontSize: '1.5rem', fontWeight: 700 },
  headerButtons: { display: 'flex', gap: '0.5rem' },
  navBtn: { padding: '0.5rem 1rem', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem' },
  subtitle: { fontSize: '1.1rem', color: '#6b7280', marginBottom: '1rem' },
  toolbar: { display: 'flex', gap: '0.5rem', marginBottom: '1rem' },
  primaryBtn: { padding: '0.5rem 1rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  refreshBtn: { padding: '0.5rem 1rem', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer' },
  filterRow: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' },
  tabInactive: { padding: '0.5rem 1rem', border: '1px solid #e5e7eb', borderRadius: '20px', background: 'white', cursor: 'pointer', fontSize: '0.85rem' },
  tabActive: { padding: '0.5rem 1rem', border: '1px solid #2563eb', borderRadius: '20px', background: '#2563eb', color: 'white', cursor: 'pointer', fontSize: '0.85rem' },
  searchInput: { padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', width: '280px' },
  showingText: { fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.75rem' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', borderBottom: '2px solid #e5e7eb', fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 },
  td: { padding: '0.75rem 1rem', borderBottom: '1px solid #f3f4f6', fontSize: '0.85rem' },
  badgeActive: { background: '#d4edda', color: '#155724', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500, display: 'inline-block' },
  badgeSubmitted: { background: '#cce5ff', color: '#004085', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500, display: 'inline-block' },
  badgeExpired: { background: '#f8d7da', color: '#721c24', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500, display: 'inline-block' },
  badgeReusable: { background: '#e2e3f1', color: '#383d6e', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 500, display: 'inline-block', marginLeft: '0.35rem' },
  actionBtn: { padding: '0.3rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '0.8rem', marginRight: '0.25rem' },
  deleteBtn: { padding: '0.3rem 0.5rem', border: '1px solid #fca5a5', borderRadius: '6px', background: '#fef2f2', cursor: 'pointer', fontSize: '0.8rem', color: '#dc2626' },
  urlCell: { fontFamily: 'monospace', fontSize: '0.78rem', color: '#4b5563', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  emptyState: { textAlign: 'center', padding: '3rem', color: '#9ca3af', fontSize: '1rem' },
  loadingState: { textAlign: 'center', padding: '3rem', color: '#6b7280', fontSize: '1rem' },
  // Modal
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '12px', padding: '2rem', width: '500px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: 600 },
  modalHeaderRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: '0 0 1.5rem 0' },
  modalTitleInline: { margin: 0, fontSize: '1.25rem', fontWeight: 600 },
  modalCloseBtn: { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6b7280', padding: '0 4px', lineHeight: 1 },
  formGroup: { marginBottom: '1rem' },
  formLabel: { display: 'block', marginBottom: '0.35rem', fontSize: '0.85rem', fontWeight: 500, color: '#374151' },
  formInput: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box' },
  formSelect: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', background: 'white' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' },
  cancelBtn: { padding: '0.5rem 1rem', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer' },
  // Pre-fill controls, mirroring the clinic Generate Link dialog.
  btnFillForm: { padding: '0.5rem 1rem', background: '#fff', color: '#2563eb', border: '1px solid #2563eb', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  prefillHint: { marginTop: '0.4rem', fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.45 },
  prefillDone: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.6rem 0.75rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px' },
  prefillDoneText: { fontSize: '0.85rem', color: '#1e40af', fontWeight: 500 },
  prefillLinkBtn: { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline', padding: '0 0.35rem' },
  // Result phase
  successBanner: { background: '#d4edda', color: '#155724', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', textAlign: 'center', fontWeight: 600, fontSize: '1rem' },
  resultUrlBox: { display: 'flex', gap: '0.5rem', marginBottom: '1rem' },
  resultInput: { flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.85rem', fontFamily: 'monospace', background: '#f9fafb' },
  expiryInfo: { fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem' },
  // Toast
  toast: { position: 'fixed', bottom: '2rem', left: '50%', transform: 'translateX(-50%)', background: '#1f2937', color: 'white', padding: '0.75rem 1.5rem', borderRadius: '8px', fontSize: '0.9rem', zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
};

export default function LinksPage() {
  const navigate = useNavigate();
  const { showAlert, showConfirm } = useModal();

  // ── Data state ──
  const [links, setLinks] = useState([]);
  const [forms, setForms] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Filter / search state ──
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // ── Toast state ──
  const [toast, setToast] = useState({ show: false, message: '' });

  // ── Generate modal state ──
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatePhase, setGeneratePhase] = useState('form'); // 'form' | 'result'
  const [generateForm, setGenerateForm] = useState({
    formId: '',
    notes: '',
    // Hong Kong calendar day + 3. toISOString() would give the UTC day, which is
    // yesterday for any time before 08:00 HKT.
    expirationDate: hkDateISOPlusDays(3),
    neverExpires: true,
    doctorEmail: '',
    ccEmail: '',
    bccEmail: '',
    reusable: true,
  });
  const [generatedResult, setGeneratedResult] = useState(null);
  const [customHost, setCustomHost] = useState('');
  const [generating, setGenerating] = useState(false);
  // Pre-fill: answers staff typed into the real form, to be attached to the link.
  const [prefillValues, setPrefillValues] = useState({});

  // ── Load data on mount ──
  useEffect(() => {
    loadData();
  }, []);

  // Coming back from "Fill Form First": restore the half-completed Generate Link
  // dialog and attach the answers just saved, so the flow continues where it left
  // off instead of starting over.
  useEffect(() => {
    const raw = sessionStorage.getItem('prefillReturn');
    if (!raw) return;
    sessionStorage.removeItem('prefillReturn');

    try {
      const ctx = JSON.parse(raw);
      const saved = sessionStorage.getItem('prefillValues');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Answers are stored against the form they were typed into. Ignore (and
        // clear) anything belonging to a different template, otherwise switching
        // templates would carry the previous form's answers across.
        if (parsed?.formId === ctx.formId) {
          setPrefillValues(parsed.values || {});
        } else {
          sessionStorage.removeItem('prefillValues');
        }
      }

      setGenerateForm({
        formId: ctx.formId || '',
        notes: ctx.notes || '',
        expirationDate: ctx.expirationDate || hkDateISOPlusDays(3),
        neverExpires: ctx.neverExpires !== undefined ? !!ctx.neverExpires : true,
        doctorEmail: ctx.doctorEmail || '',
        ccEmail: ctx.ccEmail || '',
        bccEmail: ctx.bccEmail || '',
        reusable: ctx.reusable !== undefined ? !!ctx.reusable : true,
      });
      setGeneratedResult(null);
      setCustomHost('');
      setGeneratePhase('form');
      setGenerating(false);
      setShowGenerateModal(true);
    } catch {
      // A malformed context just means no auto-reopen; nothing is lost.
      sessionStorage.removeItem('prefillValues');
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [linksData, formsData, doctorsData] = await Promise.all([
        adminService.getAllLinks(),
        adminService.getAllForms(),
        adminService.getAllDoctors(),
      ]);
      // Fix link URLs to use the current origin (works with any domain including a custom domain)
      const fixedLinks = (Array.isArray(linksData) ? linksData : []).map(link => {
        if (link.token) {
          const baseUrl = window.location.origin;
          link.url = `${baseUrl}/fill/${link.token}`;
        }
        return link;
      });
      setLinks(fixedLinks);
      setForms(Array.isArray(formsData) ? formsData : []);
      setDoctors(Array.isArray(doctorsData) ? doctorsData : []);
    } catch (error) {
      console.error('Failed to load data:', error);
      await showAlert('Failed to load links data', 'Error');
    } finally {
      setLoading(false);
    }
  };

  // ── Toast helper ──
  const showToast = useCallback((message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: '' }), 2500);
  }, []);

  // ── Filter counts ──
  const filterCounts = useMemo(() => {
    const counts = {};
    FILTER_TABS.forEach(tab => {
      counts[tab.key] = links.filter(tab.filter).length;
    });
    return counts;
  }, [links]);

  // ── Filtered + searched links ──
  const filteredLinks = useMemo(() => {
    const tab = FILTER_TABS.find(t => t.key === activeFilter) || FILTER_TABS[0];
    let result = links.filter(tab.filter);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(link =>
        (link.patientName && link.patientName.toLowerCase().includes(q)) ||
        (link.formName && link.formName.toLowerCase().includes(q)) ||
        (link.notes && link.notes.toLowerCase().includes(q)) ||
        (link.token && link.token.toLowerCase().includes(q))
      );
    }

    return result;
  }, [links, activeFilter, searchQuery]);

  // ── Status badge ──
  const renderStatusBadge = (link) => {
    const statusBadge = link.isExpired
      ? <span style={styles.badgeExpired}>Expired</span>
      : <span style={styles.badgeActive}>Active</span>;

    return statusBadge;
  };

  // ── URL display (path portion only) ──
  const getUrlPath = (url) => {
    if (!url) return '';
    try {
      const u = new URL(url);
      return u.pathname + u.search;
    } catch {
      return url;
    }
  };

  // ── Actions ──
  const handleCopy = async (url) => {
    // Only claim success when the text really reached the clipboard — the previous
    // fallback reported "copied" even when execCommand returned false.
    const copied = await copyTextToClipboard(url);
    showToast(copied ? 'Link copied!' : 'Could not copy — please copy the link manually');
  };

  const handleOpen = (url) => {
    window.open(url, '_blank');
  };

  const handleDelete = async (token) => {
    const ok = await showConfirm('Are you sure you want to delete this link?', {
      title: 'Delete Link',
      danger: true,
    });
    if (!ok) return;

    try {
      await adminService.deleteLink(token);
      showToast('Link deleted');
      await loadData();
    } catch (error) {
      console.error('Failed to delete link:', error);
      await showAlert('Failed to delete link', 'Error');
    }
  };

  // ── Generate link ──
  const openGenerateModal = () => {
    setGenerateForm({
      formId: '',
      notes: '',
      expirationDate: hkDateISOPlusDays(3),
      neverExpires: true,
      doctorEmail: '',
      ccEmail: '',
      bccEmail: '',
      reusable: true,
    });
    setGeneratedResult(null);
    setCustomHost('');
    setGeneratePhase('form');
    setPrefillValues({});
    setShowGenerateModal(true);
  };

  // Number of pre-filled answers currently attached to this link.
  const prefillCount = Object.values(prefillValues)
    .filter((v) => String(v ?? '').trim() !== '').length;

  // Opens the real form so staff can fill it in. The form is told to come back
  // here on save (prefillReturn) instead of creating a draft, so the answers end
  // up on the link we are about to generate rather than in My Submissions.
  const handleFillFormFirst = (mode = 'new') => {
    if (!generateForm.formId) return;
    // "new" means start from a blank form; "edit" keeps what was entered before.
    if (mode === 'new') sessionStorage.removeItem('prefillValues');
    sessionStorage.setItem('prefillReturn', JSON.stringify({
      formId: generateForm.formId,
      returnPath: window.location.pathname,
      notes: generateForm.notes,
      expirationDate: generateForm.expirationDate,
      neverExpires: generateForm.neverExpires,
      doctorEmail: generateForm.doctorEmail,
      ccEmail: generateForm.ccEmail,
      bccEmail: generateForm.bccEmail,
      reusable: generateForm.reusable,
    }));
    navigate(`/form/${encodeURIComponent(generateForm.formId)}?internal=1&prefill=1`);
  };

  const clearPrefill = () => {
    setPrefillValues({});
    sessionStorage.removeItem('prefillValues');
  };

  const closeGenerateModal = () => {
    setShowGenerateModal(false);
    setPrefillValues({});
    // Abandoning the dialog discards the pre-fill too, so reopening it later
    // does not resurrect answers the user chose not to use.
    sessionStorage.removeItem('prefillValues');
    sessionStorage.removeItem('prefillReturn');
  };

  const handleGenerate = async () => {
    if (!generateForm.formId) {
      await showAlert('Please select a form', 'Validation');
      return;
    }
    // Recipient is optional here, so only validate a value that was actually typed.
    if (generateForm.doctorEmail.trim() && !isValidEmail(generateForm.doctorEmail)) {
      await showAlert('Recipient Email is not a valid email address', 'Validation');
      return;
    }
    const invalidCc = findInvalidEmails(generateForm.ccEmail);
    if (invalidCc.length > 0) {
      await showAlert(`Invalid CC email: ${invalidCc.join(', ')}`, 'Validation');
      return;
    }

    const invalidBcc = findInvalidEmails(generateForm.bccEmail);
    if (invalidBcc.length > 0) {
      await showAlert(`Invalid BCC email: ${invalidBcc.join(', ')}`, 'Validation');
      return;
    }
    // `min` only constrains the picker; a typed or pasted date still gets through,
    // and a past date would otherwise be clamped to the 1-hour floor below and
    // produce a link that expires almost immediately.
    if (!generateForm.neverExpires && generateForm.expirationDate &&
        generateForm.expirationDate < hkDateISO()) {
      await showAlert('Link Expiry Date cannot be in the past', 'Validation');
      return;
    }

    try {
      setGenerating(true);
      const payload = {
        formId: generateForm.formId,
      };
      if (generateForm.notes.trim()) payload.notes = generateForm.notes.trim();
      if (!generateForm.neverExpires && generateForm.expirationDate) {
        const now = new Date();
        const selected = new Date(`${generateForm.expirationDate}T23:59:59`);
        const diffMs = selected.getTime() - now.getTime();
        payload.expirationHours = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60)));
      }
      if (generateForm.doctorEmail) payload.doctorEmail = generateForm.doctorEmail;
      if (generateForm.ccEmail.trim()) payload.ccEmail = generateForm.ccEmail.trim();
      if (generateForm.bccEmail.trim()) payload.bccEmail = generateForm.bccEmail.trim();
      if (generateForm.reusable) payload.reusable = true;
      // Only send fields staff actually filled in; blanks are dropped so they
      // never overwrite a field with an empty value.
      const cleanedPrefill = Object.fromEntries(
        Object.entries(prefillValues).filter(([, v]) => String(v ?? '').trim() !== '')
      );
      if (Object.keys(cleanedPrefill).length > 0) payload.prefillFieldValues = cleanedPrefill;

      const result = await adminService.createLink(payload);
      // Fix URL to use the current origin (works with any domain including a custom domain)
      if (result.token) {
        const baseUrl = window.location.origin;
        result.url = `${baseUrl}/fill/${result.token}`;
      }
      setGeneratedResult(result);
      setGeneratePhase('result');
      // The answers now live on the generated link; keeping the local copy would
      // silently pre-fill the next link the user creates.
      setPrefillValues({});
      sessionStorage.removeItem('prefillValues');
      // Refresh links list in background
      loadData();
    } catch (error) {
      console.error('Failed to generate link:', error);
      await showAlert(error.response?.data?.error || 'Failed to generate link', 'Error');
    } finally {
      setGenerating(false);
    }
  };

  const getDisplayUrl = () => {
    if (!generatedResult?.url) return '';
    if (!customHost.trim()) return generatedResult.url;
    try {
      const u = new URL(generatedResult.url);
      const host = customHost.trim().replace(/\/+$/, '');
      const protocol = host.startsWith('http') ? '' : u.protocol + '//';
      return (host.startsWith('http') ? host : protocol + host) + u.pathname + u.search;
    } catch {
      return generatedResult.url;
    }
  };

  const handleCopyGenerated = async () => {
    const copied = await copyTextToClipboard(getDisplayUrl());
    showToast(copied ? 'Link copied!' : 'Could not copy — please copy the link manually');
  };

  // ── Truncate helper ──
  const truncate = (str, max = 30) => {
    if (!str) return '';
    return str.length > max ? str.substring(0, max) + '…' : str;
  };

  // ── Render ──
  return (
    <div style={styles.page}>
      {/* Header bar */}
      <div style={styles.headerBar}>
        <h1 style={styles.headerTitle}>Link Management</h1>
        <div style={styles.headerButtons}>
          <button style={styles.navBtn} onClick={() => navigate('/submissions')}>
            Submitted PDFs
          </button>
          <button style={styles.navBtn} onClick={() => navigate('/admin/templates')}>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Subtitle */}
      <p style={styles.subtitle}>Patient Form Links</p>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <button style={styles.primaryBtn} onClick={openGenerateModal}>
          + Generate Link
        </button>
        <button style={styles.refreshBtn} onClick={loadData}>
          Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div style={styles.filterRow}>
        {FILTER_TABS.map(tab => (
          <button
            key={tab.key}
            style={activeFilter === tab.key ? styles.tabActive : styles.tabInactive}
            onClick={() => setActiveFilter(tab.key)}
          >
            {tab.label} ({filterCounts[tab.key] || 0})
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
        <input
          type="text"
          placeholder="Search links..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Showing count */}
      <p style={styles.showingText}>Showing {filteredLinks.length} links</p>

      {/* Loading */}
      {loading && <div style={styles.loadingState}>Loading...</div>}

      {/* Table */}
      {!loading && filteredLinks.length === 0 && (
        <div style={styles.emptyState}>No links found</div>
      )}

      {!loading && filteredLinks.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Form</th>
                <th style={styles.th}>Recipient Email</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Link URL</th>
                <th style={styles.th}>Created</th>
                <th style={styles.th}>Expires</th>
                <th style={styles.th}>Notes</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLinks.map((link) => (
                <tr key={link.token} style={{ transition: 'background 0.15s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={styles.td}>{link.formName || '—'}</td>
                  <td style={styles.td}>{link.doctorEmail || '—'}</td>
                  <td style={styles.td}>{renderStatusBadge(link)}</td>
                  <td style={{ ...styles.td, ...styles.urlCell }} title={link.url}>
                    {getUrlPath(link.url)}
                  </td>
                  <td style={styles.td}>{formatDate(link.createdAt)}</td>
                  <td style={styles.td}>{formatDate(link.expiresAt)}</td>
                  <td style={styles.td} title={link.notes || ''}>
                    {truncate(link.notes)}
                  </td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                    <button style={styles.actionBtn} onClick={() => handleCopy(link.url)} title="Copy link">
                      Copy
                    </button>
                    <button style={styles.actionBtn} onClick={() => handleOpen(link.url)} title="Open link">
                      Open
                    </button>
                    <button style={styles.deleteBtn} onClick={() => handleDelete(link.token)} title="Delete link">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Generate Link Modal ── */}
      {showGenerateModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            {generatePhase === 'form' ? (
              <>
                <div style={styles.modalHeaderRow}>
                  <h2 style={styles.modalTitleInline}>Generate Form Link</h2>
                  <button type="button" style={styles.modalCloseBtn} onClick={closeGenerateModal} aria-label="Close">&times;</button>
                </div>

                {/* Form select */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Form *</label>
                  <select
                    style={styles.formSelect}
                    value={generateForm.formId}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, formId: e.target.value }))}
                  >
                    <option value="">— Select a form —</option>
                    {forms.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Notes</label>
                  <input
                    type="text"
                    style={styles.formInput}
                    placeholder="Separate multiple emails with comma"
                    value={generateForm.notes}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, notes: e.target.value }))}
                  />
                </div>

                {/* Expiration */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Link Expiration</label>
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <input
                      type="date"
                      style={{ ...styles.formInput, flex: 1, opacity: generateForm.neverExpires ? 0.6 : 1 }}
                      value={generateForm.expirationDate}
                      min={hkDateISO()}
                      disabled={generateForm.neverExpires}
                      onChange={(e) => setGenerateForm(prev => ({ ...prev, expirationDate: e.target.value }))}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={generateForm.neverExpires}
                        onChange={(e) => setGenerateForm(prev => ({ ...prev, neverExpires: e.target.checked }))}
                      />
                      Never expires
                    </label>
                  </div>
                </div>

                {/* Recipient Email */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Recipient Email</label>
                  <input
                    type="email"
                    style={styles.formInput}
                    placeholder="Enter recipient email"
                    value={generateForm.doctorEmail}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, doctorEmail: e.target.value }))}
                    list="doctor-email-suggestions-links-page"
                  />
                  <datalist id="doctor-email-suggestions-links-page">
                    {doctors.map(d => (
                      <option key={d.id} value={d.email}>{d.name}</option>
                    ))}
                  </datalist>
                </div>

                {/* CC Email - visible to every recipient */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>CC Email</label>
                  <input
                    type="email"
                    multiple
                    style={styles.formInput}
                    placeholder="Separate multiple emails with comma"
                    value={generateForm.ccEmail}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, ccEmail: e.target.value }))}
                  />
                </div>

                {/* BCC Email - hidden from other recipients */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>BCC Email</label>
                  <input
                    type="email"
                    multiple
                    style={styles.formInput}
                    placeholder="Separate multiple emails with comma"
                    value={generateForm.bccEmail}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, bccEmail: e.target.value }))}
                  />
                </div>

                {/* Reusable checkbox */}
                <label
                  style={{
                    ...styles.checkboxRow,
                    padding: '0.9rem 1rem',
                    border: generateForm.reusable ? '1.5px solid #2563eb' : '1px solid #d1d5db',
                    borderRadius: '10px',
                    background: generateForm.reusable ? '#eff6ff' : '#f9fafb',
                    alignItems: 'flex-start',
                    gap: '0.75rem'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={generateForm.reusable}
                    onChange={(e) => setGenerateForm(prev => ({ ...prev, reusable: e.target.checked }))}
                    style={{ width: '16px', height: '16px', marginTop: '0.15rem' }}
                  />
                  <span>
                    <strong style={{ display: 'block', marginBottom: '0.15rem', color: '#111827' }}>Reusable link</strong>
                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>Can be submitted multiple times</span>
                  </span>
                </label>

                {/* Optional pre-fill. Staff open the real form, fill in whatever the
                    patient should not have to retype, and save; those answers are
                    attached to the link so the patient sees them already filled in. */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Pre-fill (optional)</label>
                  {!generateForm.formId ? (
                    <div style={styles.prefillHint}>Select a form first to pre-fill it.</div>
                  ) : prefillCount > 0 ? (
                    <div style={styles.prefillDone}>
                      <span style={styles.prefillDoneText}>
                        {prefillCount} field{prefillCount === 1 ? '' : 's'} pre-filled
                      </span>
                      <span>
                        <button type="button" style={styles.prefillLinkBtn} onClick={() => handleFillFormFirst('edit')}>
                          Edit
                        </button>
                        <button type="button" style={styles.prefillLinkBtn} onClick={clearPrefill}>
                          Clear
                        </button>
                      </span>
                    </div>
                  ) : (
                    <>
                      <button type="button" style={styles.btnFillForm} onClick={() => handleFillFormFirst('new')}>
                        Pre-fill Form
                      </button>
                      <div style={styles.prefillHint}>
                        Pre-fill fields on the form if needed. Your entries will be saved
                        and shown to the patient when they open the link.
                      </div>
                    </>
                  )}
                </div>

                {/* Footer */}
                <div style={styles.modalFooter}>
                  <button style={styles.cancelBtn} onClick={closeGenerateModal}>
                    Cancel
                  </button>
                  <button
                    style={{ ...styles.primaryBtn, opacity: generating ? 0.6 : 1 }}
                    onClick={handleGenerate}
                    disabled={generating}
                  >
                    {generating ? 'Generating...' : 'Generate Link'}
                  </button>
                </div>
              </>
            ) : (
              /* ── Result phase ── */
              <>
                <div style={styles.modalHeaderRow}>
                  <h2 style={styles.modalTitleInline}>Link Generated</h2>
                  <button type="button" style={styles.modalCloseBtn} onClick={closeGenerateModal} aria-label="Close">&times;</button>
                </div>
                <div style={styles.successBanner}>Link Generated Successfully!</div>

                {/* Generated URL */}
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Generated URL</label>
                  <div style={styles.resultUrlBox}>
                    <input
                      type="text"
                      style={styles.resultInput}
                      value={getDisplayUrl()}
                      readOnly
                    />
                    <button style={styles.primaryBtn} onClick={handleCopyGenerated}>
                      Copy
                    </button>
                  </div>
                </div>

                {/* Expiry info */}
                <p style={styles.expiryInfo}>
                  {generatedResult?.expiresAt
                    ? `Expires: ${formatDate(generatedResult.expiresAt)}`
                    : 'This link never expires'}
                </p>

                {/* Footer */}
                <div style={styles.modalFooter}>
                  <button
                    style={styles.cancelBtn}
                    onClick={() => {
                      setGeneratePhase('form');
                      setGeneratedResult(null);
                      setCustomHost('');
                      setGenerateForm(prev => ({ ...prev, notes: '' }));
                    }}
                  >
                    Generate Another
                  </button>
                  <button style={styles.primaryBtn} onClick={closeGenerateModal}>
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast.show && (
        <div style={styles.toast}>{toast.message}</div>
      )}
    </div>
  );
}
